import { ChangeDetectorRef, Component, effect, inject, Input, input, OnInit, output, signal } from '@angular/core';
import { IActionButton, Work } from '../../../models/work.model';
import { ConfirmDialogComponent } from '../../../main/confirm-dialog/confirm-dialog.component';
import { SignComponent } from '../../../shared/sign/sign.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { WorkService } from '../../work.service';
import { FormGroup } from '@angular/forms';
import { catchError, first, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { WorkDialogComponent } from '../work-dialog.component';
import { WorkNextComponent } from '../../work-next/work-next.component';
import { Router } from '@angular/router';
import { SetStatusComponent } from '../../set-status/set-status.component';
import { AssignUserComponent } from '../../assign-user/assign-user.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WorkRejectionComponent } from '../../work-rejection/work-rejection.component';
import { Status } from '../../../models/status.model';
import { AdminService } from '../../../admin/services/admin.service';

@Component({
  selector: 'app-work-dialog-footer',
  standalone: false,
  templateUrl: './work-dialog-footer.component.html',
  styleUrls: ['./work-dialog-footer.component.scss', '../work-dialog.component.scss']
})
export class WorkDialogFooterComponent implements OnInit {

  actionButtons = input<IActionButton[]>(null);
  
  clientMode = input<boolean>(false);

  @Input() chatMode = false;

  form = input.required<FormGroup>();

  hasPendingPlaceholder = input<boolean>(false);

  isReadOnly = input<boolean>(true);

  isRevisor = input<boolean>(false);

  updating = signal(false);

  statusChanged = output<number>();

  waitingRevision = input(false);

  work = input.required<Work>();


  private readonly adminService = inject(AdminService);

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly dialog = inject(MatDialog);

  private readonly dialogRef = inject(MatDialogRef<WorkDialogComponent>);

  private readonly router = inject(Router);

  private readonly snackBar = inject(MatSnackBar);
  
  private readonly workService = inject(WorkService);

  public cannotComplete = signal(false);

  constructor() {
    
    effect(() => {
      this.updateCannotComplete();
    });
  }
  ngOnInit(): void {
    this.updateCannotComplete();
    this.form().valueChanges.subscribe(() => {
      this.updateCannotComplete();
      this.cdr.markForCheck();
    });
  }

  get ancestors() {
    let ancestors = [];
    let parent = this.work().document;
    while (parent) {
      ancestors.push({...parent, type: parent.base_type});
      parent = parent.document;
    }
    return ancestors;
  }

  updateCannotComplete() {
    this.cannotComplete.set(!!this.work()?.completed_at
      || this.remainingWork > 0
      || !this.work().user_id
      || !this.form().valid
      || (this.work().work && this.work().work.min_photos > this.work().pictures.length)
      || this.hasPendingPlaceholder()
      || (this.work().revisor_id && !this.isRevisor())
    );
  }

  get cannotReject() {
    return this.work().completed_at
      || !this.work().user_id
      || this.clientMode()
      || (!this.isRevisor() && this.work().revisor_id);
  }

  get requiresRevision() {
    return this.work().work && this.work().work.require_revision && (!this.work().revisor_id || !this.isRevisor());
  }

  get remainingWork() {
    if (this.work()?.check_list) {
      const total = this.work().work_statuses.length;
      const completed = this.work().work_statuses.filter((i) => i.completed || i.value != null).length;
      return total - completed;
    }
    return 0;
  }

  complete(customFn?: () => Promise<void>) {
    if (this.form().valid) {
      let body = '¿Desea continuar?';
      if (this.work().has_notifications) {
        body += ' Se enviará una notificación al usuario';
      }
      if (this.work().work && this.work().work.sign_needed) {
        this.dialog.open(SignComponent, { data: { sign_text: this.work().work.sign_text } }).afterClosed().toPromise().then((result) => {
          if (result) {
            this.workService.update(this.work().id, { sign_url: result }).subscribe(() => {
              if (customFn) {
                customFn().then(() => {
                  this.nextWork();
                });
              } else {
                this.nextWork();
              }
            });
          }
        });
      } else {
        this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: 'Finalizar Trabajo',
            body,
          },
        }).afterClosed().toPromise().then((result) => {
          if (result) {
            if (customFn) {
              customFn().then(() => {
                this.nextWork();
              });
            } else {
              this.nextWork();
            }
          }
        });
      }
    }
  }

  reject() {
    if (this.waitingRevision() && this.isRevisor()) {
      this.workService.reject(this.work().id, {
        reason: 'revision_rejected',
        comment: 'No cumple con los requisitos mínimos',
      }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          this.snackBar.open('Error al rechazar trabajo', 'Cerrar', { duration: 2000 });
          return of(null);
        }),
      ).subscribe((response) => {
        if (!response) return;

        if (this.dialogRef instanceof MatDialogRef) this.dialogRef.close();
        else this.router.navigate(['main', 'work']);
      });

    } else {
      this.dialog.open(WorkRejectionComponent).afterClosed().pipe(first()).forEach((form) => {
        if (form) {
          this.workService.reject(this.work().id, form).pipe(
            catchError((error: HttpErrorResponse) => {
              console.log(error);
              this.snackBar.open('Error al rechazar trabajo', 'Cerrar', { duration: 2000 });
              return of(null);
            }),
          ).subscribe((response) => {
            if (!response) return;

            if(this.dialogRef instanceof MatDialogRef) this.dialogRef.close();
          });
        }
      });
    }
  }

  onSetStatus(status: Status) {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: "Mover a Estado",
        body: `¿Desea mover ${this.adminService.getText(this.work().document?.type) || 'documento'} al estado ${status.name}?`
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.setStatus(status.id);
      }
    });
  }

  setStatus(status_id: number){
    this.updating.set(true);
    this.workService.updateStatus(this.work().id, {status_id,...this.form().value}).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return of(null);
      }),
    ).subscribe((response) => {
      this.updating.set(false);
      if (!response) return;
      this.cdr.markForCheck();
      this.work().completed_at = new Date();
      if(this.dialogRef instanceof MatDialogRef) this.dialogRef.close();
    });
  }

  nextWork() {
    if (this.work().work && this.work().work.next_custom_work_ids && this.work().work.next_custom_work_ids.length !== 0) {
      if (this.work().work.next_custom_work_ids.length === 1) {
        this.form().patchValue({ next_custom_work_id: this.work().work.next_custom_work_ids[0] });
        this.finalize();
      } else {
        this.dialog.open(WorkNextComponent, {
          data: { work_ids: this.work().work.next_custom_work_ids },
        })
          .afterClosed().subscribe((value) => {
            if (value) {
              this.form().patchValue({ next_custom_work_id: value });
              this.finalize();
            }
          });
      }
    } else {
      this.finalize();
    }
  }

  finalize() {
    this.workService.complete(this.work().id, this.form().value).pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return of(null);
      }),
    ).subscribe((response) => {
      if (!response) return;

      if (!this.requiresRevision) {
        this.cdr.markForCheck();
        this.work().completed_at = new Date();
        this.changeStatus();
      } else if (this.dialogRef instanceof MatDialogRef) {
        this.dialogRef.close();
      } else {
        this.router.navigate(['main', 'work']);
      }
    });
  }

  changeStatus() {
    if (this.work().work && this.work().work.can_change_status) {
      this.dialog.open(SetStatusComponent, {
        disableClose: true,
        data: {
          document_id: this.work().document_id,
          document_type: this.work().document_type,
          status_id: this.work().work.status_id,
          work_instance_id: this.work().id,
        },
      }).afterClosed().subscribe(() => {
        this.work().completed_at = new Date();
        if(this.dialogRef instanceof MatDialogRef) this.dialogRef.close();
      });
    } else {
      this.work().completed_at = new Date();
      if(this.dialogRef instanceof MatDialogRef) this.dialogRef.close();
    }
  }

  setUser() {
    this.dialog.open(AssignUserComponent, {
      data: this.work().work ? this.work().work.roles_id : this.work().roles_id,
    }).afterClosed().subscribe((value) => {
      if (value) {
        this.cdr.markForCheck();
        this.work().user_id = value.id;
        this.workService.update(this.work().id, { user_id: value.id }).pipe(
          catchError((error: HttpErrorResponse) => {
            console.log(error);
            this.snackBar.open('Error al asignar usuario', 'Cerrar', { duration: 2000 });
            return of(null);
          }),
        ).subscribe((response) => {
          if (!response) return;

          if(this.dialogRef instanceof MatDialogRef) this.dialogRef.close();
        });
      }
    });
  }
}
